import axios from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import { getApiBaseUrl } from './axiosInstance';

// Phase 8 (PAYLINK-16, D-06/D-07) — the credential seam for pay-by-link.
//
// `payClient` is a dedicated `axios.create()` sibling of `apiClient`. The pay
// session credential rides as a `?temp_token=` query param on the SHARED
// `/v2/invoices/:id` URL — the exact URL the authenticated app also calls — so a
// URL-keyed interceptor guard on the shared client cannot isolate a pay-page
// call from an authed one. Isolation therefore lives in a separate instance:
//
//   - D-06a: NEVER reads stored auth (localStorage); no Authorization header is
//     ever attached, even when a signed-in user's token is present (a signed-in
//     user's header on the exchange would trip a 409 session_conflict).
//   - D-06c: always sends `Accept: application/vnd.serviceshift.com; version=2`.
//   - D-06d: reuses getApiBaseUrl() for base-URL/tenant resolution (imported
//     from axiosInstance — the hostname logic is NOT duplicated here).
//   - D-07: its OWN response handler rejects with a typed PayApiError and NEVER
//     invokes the shared client's unauthorized handler / bounces to /login. A
//     bad-token 401/404/410/409/429 renders the in-view privacy state, never the
//     app's session-expiry / login-bounce path.
//   - D-05: holds NO module-global token; the temp_token is threaded per-call by
//     callers, keeping the client stateless.
//
// The shared interceptor (axiosInstance.ts) stays byte-identical — do NOT edit
// it for pay-by-link (RESEARCH Pitfall 1).

export const PAY_ACCEPT_HEADER = 'application/vnd.serviceshift.com; version=2';

export const payClient = axios.create({
  headers: { Accept: PAY_ACCEPT_HEADER },
});

payClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // Reuse the shared base-URL/tenant resolution (D-06d). Mirrors apiClient,
    // but with NO stored-auth read and NO Authorization header (D-06a).
    config.baseURL = getApiBaseUrl();

    if (
      import.meta.env.VITE_LOCAL_API_URL &&
      import.meta.env.VITE_LOCAL_TENANT
    ) {
      config.headers['Tenant'] = import.meta.env.VITE_LOCAL_TENANT;
    }

    return config;
  },
);

// Typed transport error surfaced by the scoped client's response handler. The
// app-level resolver maps this to the PayTokenResolution union (Phase 8 folds
// every error branch to the single privacy view; the errorCode is captured for
// Phase 9/10).
//
// Phase 9 (PAYLINK-05/PAYLINK-07, D-01): `response` preserves the ORIGINAL axios
// error response (status + body) additively. `classifyPaymentError`
// (utils/payment-error.ts) reads `.response.data.message` and `.response.status`,
// so without this a bank decline (402) received over the token misclassified as
// `system` and the CR-01 refreshPaymentKey retry never fired (RESEARCH Pitfall
// 1). `status`/`errorCode` are UNCHANGED — PayView reads only `.errorCode`
// (PayView.vue:204), so the extra field is backward-compatible. This is a
// pay-seam edit allowed by Phase 8 D-06; the shared interceptor axiosInstance.ts
// stays byte-identical (RESEARCH Pitfall 5). The preserved body adds no new
// Sentry surface — the charge URL `temp_token` query stays covered by
// redactPayUrl (Phase 8 D-09), and the raw response object is never logged here.
export interface PayApiError {
  status: number;
  errorCode: string | null;
  response?: PayResponseError['response'];
}

type PayResponseError = {
  response?: {
    status?: number;
    data?: { error_code?: string; message?: string };
  };
};

payClient.interceptors.response.use(
  (response) => response,
  (error: PayResponseError) => {
    // D-07: never invoke the shared client's unauthorized handler; never
    // dispatch auth:session-expired; never navigate to /login. Normalize the
    // transport error to a typed PayApiError and reject.
    const status = error.response?.status ?? 0;
    const errorCode = error.response?.data?.error_code ?? null;
    return Promise.reject<never>({
      status,
      errorCode,
      response: error.response,
    } satisfies PayApiError);
  },
);
